import { DatePicker, Select, Space } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useAuditActorOptions } from "../hooks/useAudit";
import type { AuditFilterState } from "../types";
import { amalGuruhlari, RESURS_TURLARI } from "../utils/actions";

const { RangePicker } = DatePicker;

const ACTOR_SEARCH_DEBOUNCE_MS = 300;

interface AuditFiltersProps {
	value: AuditFilterState;
	onChange: (next: AuditFilterState) => void;
}

/**
 * Audit filtr paneli.
 *
 * ⚠️ Sana oralig'i UTC bo'yicha talqin qilinadi (backend `audit.repository`
 * izohi) — bu ko'rish qulayligi uchun filtr, biznes sanasi emas.
 */
export function AuditFilters({ value, onChange }: AuditFiltersProps) {
	const { t } = useTranslation("audit");
	const [actorSearch, setActorSearch] = useState("");
	const debouncedActorSearch = useDebouncedValue(actorSearch, ACTOR_SEARCH_DEBOUNCE_MS);
	const { data: actors = [], isFetching } = useAuditActorOptions(debouncedActorSearch);

	const range: [dayjs.Dayjs, dayjs.Dayjs] | null =
		value.dateFrom && value.dateTo ? [dayjs(value.dateFrom), dayjs(value.dateTo)] : null;

	return (
		<Space wrap className="mb-4">
			{/* ⚠️ Qiymat — `userId`, xodim id si EMAS (`useAuditActorOptions` izohi). */}
			<Select
				allowClear
				placeholder={t("filters.actorPlaceholder")}
				style={{ width: 240 }}
				options={actors.map((actor) => ({ value: actor.userId, label: actor.fullName }))}
				value={value.actorUserId}
				showSearch={{ filterOption: false, onSearch: setActorSearch }}
				loading={isFetching}
				notFoundContent={isFetching ? t("filters.actorSearching") : t("filters.actorNotFound")}
				onChange={(actorUserId) => onChange({ ...value, actorUserId })}
			/>
			<Select
				allowClear
				placeholder={t("filters.resourceTypePlaceholder")}
				style={{ width: 200 }}
				options={RESURS_TURLARI.map((tur) => ({ value: tur, label: tur }))}
				value={value.resourceType}
				showSearch={{ optionFilterProp: "label" }}
				onChange={(resourceType) => onChange({ ...value, resourceType })}
			/>
			{/* ⚠️ Ikki guruh — HTTP va meros domen amallari (kontrakt §4.4). */}
			<Select
				allowClear
				placeholder={t("filters.actionPlaceholder")}
				style={{ width: 240 }}
				options={amalGuruhlari(t)}
				value={value.action}
				showSearch={{ optionFilterProp: "label" }}
				onChange={(action) => onChange({ ...value, action })}
			/>
			<RangePicker
				value={range}
				onChange={(next) =>
					onChange({
						...value,
						dateFrom: next?.[0] ? next[0].format("YYYY-MM-DD") : undefined,
						dateTo: next?.[1] ? next[1].format("YYYY-MM-DD") : undefined,
					})
				}
			/>
		</Space>
	);
}
